import { useEffect, useRef } from 'react';

import StatusCode from 'status-code-enum';

import { adWriteApi } from 'api/adWriteApi';

import { format422ErrorsFromApiForForm } from 'helpers/forms';
import { fireToast } from 'helpers/Toasts';
import { useBraze } from 'hooks/useBraze';
import { pushToDataLayer } from 'services/gtmService';

import type { IEnquiryData } from 'components/PageWrappers/AdDetails/components/DealerEnquiryModal/DealerEnquiryModal.config';
import { MILEAGE_UNIT } from 'types';
import type { UseDealerEnquiryModal } from 'components/PageWrappers/AdDetails/components/DealerEnquiryModal/DealerEnquiryModal.typed';
import { useAdDetailsContext } from 'contexts/AdDetailsContext';
import { rg4js } from 'helpers/raygun';
import { PAGE } from 'helpers/pages';

function useDealerEnquiryModal(props: UseDealerEnquiryModal) {
  const {
    enquiry,
    askToClose,
    includesDefaultMessageText,
    adId,
    authorizationHeader,
    sectionName,
    resetModal,
    setIsSubmitting,
    setDisableSubmitButton,
    tradeInValues,
    disableVehicleRegLookupButton,
    onLookupSuccess,
    onLookupFailure,
    populateModal,
    setIsPostEnquirySimilarAdsModalOpen,
    setSimilarAds,
  } = props;
  const { logBrazeSendDealerEnquiryEvent } = useBraze();
  const { setIsDealerEnquiryModalOpen } = useAdDetailsContext();
  // TODO: Import props available from context

  const resultsRef = useRef<HTMLDivElement>(null);

  const getSimilarAds = async () => {
    try {
      const { status, data } = await adWriteApi.getPostEnquirySimilarAds(
        adId,
        authorizationHeader,
      );
      handleClose();
      if (status === StatusCode.SuccessOK) {
        setSimilarAds(data);
        setIsDealerEnquiryModalOpen(false);
        setIsPostEnquirySimilarAdsModalOpen(true);
      } else {
        rg4js('send', {
          error: new Error(
            'Get similar ads did not return the expected status code',
          ),
          tags: [PAGE.AD_DETAILS],
          customData: { status },
        });
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
      }
    } catch (error) {
      rg4js('send', {
        error,
        tags: [PAGE.AD_DETAILS],
        customData: { functionName: 'getSimilarAds' },
      });
      askToClose();
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
    }
  };

  const onSendEnquiry = async (
    values: IEnquiryData,
    includesDefaultMessageText: boolean,
  ) => {
    try {
      const sendEnquiryPayload = { id: adId, ...values };
      const { status } = await adWriteApi.sendEnquiry(
        sendEnquiryPayload,
        authorizationHeader,
      );
      if (status === StatusCode.SuccessCreated) {
        pushToDataLayer({
          event: 'message_sent',
          message_text: values.message,
          default_message_text: includesDefaultMessageText,
        });
        logBrazeSendDealerEnquiryEvent(sectionName);
        getSimilarAds();
      } else {
        setIsSubmitting(false);
        askToClose();
        rg4js('send', {
          error: new Error('Error sending enquiry'),
          tags: [PAGE.AD_DETAILS],
          customData: { functionName: 'onSendEnquiry' },
        });
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
      }
    } catch (error) {
      rg4js('send', {
        error,
        customData: { functionName: 'onSendEnquiry' },
        tags: [PAGE.AD_DETAILS],
      });
      fireToast({
        type: 'ERROR',
        text: 'Sorry we couldn’t send your enquiry right now, please try again later',
      });
      setIsSubmitting(false);
      askToClose();
      if (
        error?.response?.status === StatusCode.ClientErrorUnprocessableEntity
      ) {
        return format422ErrorsFromApiForForm(error.response.data);
      }
    }
  };

  const onSubmit = async (formValues: IEnquiryData) => {
    const {
      contactName,
      contactNo = '',
      email,
      message,
      financingVehicle = false,
      schedulingTestDrive = false,
      vehicleTradeIn = false,
      vehicleCondition = false,
      saveEnquiry = false,
      registrationNo = '',
      mileage = '',
      mileageType = MILEAGE_UNIT.KILOMETRES,
    } = formValues;

    setIsSubmitting(true);

    let updatedValues: IEnquiryData = {
      contactName,
      contactNo,
      email,
      message,
      financingVehicle,
      schedulingTestDrive,
      vehicleTradeIn,
      vehicleCondition,
      saveEnquiry,
    };
    // Only send trade in values if a vehicle lookup was performed successfully
    if (disableVehicleRegLookupButton) {
      updatedValues = {
        ...updatedValues,
        registrationNo,
        ...tradeInValues,
        // Placed here to overwrite default mileage values returned in tradeInValues
        mileage,
        mileageType,
      };
    }

    const response = await onSendEnquiry(
      updatedValues,
      includesDefaultMessageText,
    );

    return response;
  };

  const handleDataFromVehicleLookup = async (value: string) => {
    if (value.length === 0) {
      onLookupFailure('Please enter your trade in registration');
      return;
    }
    try {
      const { data } = await adWriteApi.lookupVehicleRegistrationNumber(
        value,
        authorizationHeader,
      );
      if (data.make) {
        onLookupSuccess(data);
        if (resultsRef && resultsRef.current) {
          resultsRef.current.scrollIntoView({
            behavior: 'smooth',
          });
        }
      } else {
        onLookupFailure('Sorry, no details found. Please try again!');
      }
    } catch (error) {
      rg4js('send', {
        error,
        tags: [PAGE.AD_DETAILS],
        customData: { functionName: 'onSendEnquiry' },
      });
      fireToast({
        type: 'ERROR',
        text: 'Sorry we couldn’t send your registration details right now, please try again later',
      });
    }
  };

  const handleClose = () => {
    resetModal();
    askToClose();
  };

  useEffect(() => {
    const timer = setTimeout(() => setDisableSubmitButton(false), 500);
    return () => clearTimeout(timer);
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (enquiry.make && enquiry.model) {
      populateModal(enquiry);
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    onSendEnquiry,
    onSubmit,
    handleDataFromVehicleLookup,
    handleClose,
    resultsRef,
  };
}

export { useDealerEnquiryModal };
